/**
 * SyslogAdapter.ts - Syslog Adapter
 * 
 * CHANGE HISTORY:
 * - 251226_0855 [active] danielbeer: Remove @ts-nocheck and fix type errors
 * - 251021_0517 [8786ca9] danielbeer: feat: apply authr pattern to all 43 remaining packages
 * - 251021_0447 [ce7b5e6] danielbeer: feat: automate TypeScript and Actr integration for all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import { LogLevel, LogEntry } from '../models/LogEntry.js';
import { type Actr } from '@opcentral/typr-lib';

/**
 * SyslogAdapter class
 */
interface SyslogOptions {
  minLevel?: number;
  host?: string;
  port?: number;
  protocol?: 'udp' | 'tcp';
  facility?: number;
  [key: string]: any;
}

export class SyslogAdapter {
  options: SyslogOptions;
  connected: boolean;
  socket: any;

  constructor(options: SyslogOptions = {}) {
    this.options = {
      minLevel: options.minLevel !== undefined ? options.minLevel : LogLevel.WARN,
      host: options.host || 'localhost',
      port: options.port || 514,
      protocol: options.protocol || 'udp', // udp or tcp
      facility: options.facility || 1, // user
      ...options
    };
    
    this.connected = false;
    this.socket = null;
  }
  
  /**
   * Connect to syslog server
   */
  async connect(): Promise<void> {
    if (this.connected) return;
    
    // In browser, use WebSocket or HTTP
    if (typeof window !== 'undefined') {
      // Browser implementation would use HTTP endpoint
      this.connected = true;
      return;
    }
    
    // In Node.js, use dgram or net
    if (typeof require !== 'undefined') {
      try {
        if (this.options.protocol === 'udp') {
          const dgram = require('dgram');
          this.socket = dgram.createSocket('udp4');
        } else {
          const net = require('net');
          this.socket = net.createConnection({
            host: this.options.host,
            port: this.options.port
          });
        }
        this.connected = true;
      } catch (error) {
        console.error('Failed to connect to syslog:', error);
      }
    }
  }
  
  /**
   * Write log entry
   */
  async write(logEntry: LogEntry): Promise<void> {
    if (this.options.minLevel !== undefined && !logEntry.isLevelEnabled(this.options.minLevel as any)) {
      return;
    }
    
    await this.connect();
    
    const message = logEntry.toSyslogString();
    
    try {
      if (typeof window !== 'undefined') {
        // Browser: Send to HTTP endpoint
        await this.sendToHTTP(message);
      } else if (this.socket) {
        // Node.js: Send to syslog server
        if (this.options.protocol === 'udp') {
          this.socket.send(
            Buffer.from(message),
            this.options.port,
            this.options.host
          );
        } else {
          this.socket.write(message + '\n');
        }
      }
    } catch (error) {
      console.error('Failed to send to syslog:', error);
    }
  }
  
  /**
   * Send to HTTP endpoint (for browser)
   */
  async sendToHTTP(message: string): Promise<void> {
    try {
      await fetch('/api/logs/syslog', {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: message
      });
    } catch (error) {
      console.error('Failed to send to HTTP syslog endpoint:', error);
    }
  }
  
  /**
   * Close connection
   */
  close(): void {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    this.connected = false;
  }
}

export default SyslogAdapter;
